/**
 * Generische Slider-Klasse mit horizontaler Scroll-Logik
 */
export class GenericSlider {
  constructor(hostElement, config) {
    this.host = hostElement;
    this.config = config;
    this.clickHandlersAttached = false;
    this.isScrollable = false;
    this.intersectionObserver = null;
    this.resizeObserver = null;
    this.headerContainer = null;
  }

  init() {
    this.getElements();
    this.checkIfScrollable();

    if (this.isScrollable) {
      this.initControls();
      this.addEvents();
      this.clickHandlersAttached = true;
      this.observeEdgeItems();
    }

    this.updateHeaderVisibility();

    if (this.config.enableResize) {
      this.observeResize();
    }
  }

  getElements() {
    this.list = this.host.querySelector(this.config.listSelector);
    this.items = this.host.querySelectorAll(this.config.itemSelector);
    this.controls = this.host.querySelector(this.config.controlsSelector);
    this.prevButton = this.host.querySelector(this.config.prevButtonSelector);
    this.nextButton = this.host.querySelector(this.config.nextButtonSelector);
    this.headerContainer = this.host.querySelector('.header-navi-container');
  }

  checkIfScrollable() {
    if (!this.list) return;

    this.isScrollable = this.list.scrollWidth > this.list.offsetWidth;

    if (this.isScrollable && this.controls) {
      this.controls.classList.add('active');
    } else if (!this.isScrollable) {
      if (this.prevButton) this.prevButton.disabled = true;
      if (this.nextButton) this.nextButton.disabled = true;
    }
  }

  updateHeaderVisibility() {
    if (!this.headerContainer) return;

    const hasHeadline = this.headerContainer.querySelector('.kopfzeile');
    const hasActiveControls = this.isScrollable && window.innerWidth >= 0;

    if (hasHeadline || hasActiveControls) {
      this.headerContainer.classList.add('has-visible-content');
    } else {
      this.headerContainer.classList.remove('has-visible-content');
    }
  }

  initControls() {
    if (this.prevButton) {
      this.prevButton.disabled = true;
    }
    if (this.nextButton) {
      this.nextButton.disabled = false;
    }
  }

  addEvents() {
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => {
        this.scrollPrevious();
      });
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => {
        this.scrollNext();
      });
    }
  }

  get singleEntryWidth() {
    if (!this.items.length) return 0;

    const element = this.items[0];
    const elementStyle = element.currentStyle || window.getComputedStyle(element);
    const breite = element.offsetWidth;
    const margin = parseFloat(elementStyle.marginRight) + parseFloat(elementStyle.marginLeft);
    const border = parseFloat(elementStyle.borderRightWidth) + parseFloat(elementStyle.borderLeftWidth);

    return breite + margin + border;
  }

  scrollPrevious() {
    const scrollDistance = this.singleEntryWidth * this.config.scrollMultiplier;
    this.scrollToPosition(this.list.scrollLeft - scrollDistance);
  }

  scrollNext() {
    const scrollDistance = this.singleEntryWidth * this.config.scrollMultiplier;
    this.scrollToPosition(this.list.scrollLeft + scrollDistance);
  }

  scrollToPosition(position) {
    if (!this.list) return;

    this.list.scrollTo({
      left: position,
      behavior: 'smooth'
    });
  }

  observeEdgeItems() {
    if (!this.items.length) return;

    this.firstItem = this.items[0];
    this.lastItem = this.items[this.items.length - 1];

    const options = {
      root: this.list,
      threshold: this.config.visibilityThreshold
    };

    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const fullyVisible = entry.isIntersecting && entry.intersectionRatio >= this.config.visibilityRatio;

        if (entry.target === this.firstItem && fullyVisible) {
          if (this.prevButton) this.prevButton.disabled = true;
        } else if (entry.target === this.firstItem) {
          if (this.prevButton) this.prevButton.disabled = false;
        }

        if (entry.target === this.lastItem && fullyVisible) {
          if (this.nextButton) this.nextButton.disabled = true;
        } else if (entry.target === this.lastItem) {
          if (this.nextButton) this.nextButton.disabled = false;
        }
      });
    }, options);

    this.intersectionObserver.observe(this.firstItem);
    this.intersectionObserver.observe(this.lastItem);
  }

  observeResize() {
    if (!this.list) return;

    this.resizeObserver = new ResizeObserver(() => {
      this.handleResize();
    });

    this.resizeObserver.observe(this.list);
  }

  handleResize() {
    const wasScrollable = this.isScrollable;
    this.isScrollable = this.list.scrollWidth > this.list.offsetWidth;

    if (this.isScrollable && !wasScrollable) {
      if (this.controls) this.controls.classList.add('active');
      this.initControls();
      if (!this.clickHandlersAttached) {
        this.addEvents();
        this.clickHandlersAttached = true;
      }
      if (!this.intersectionObserver) {
        this.observeEdgeItems();
      }
    } else if (!this.isScrollable && wasScrollable) {
      if (this.controls) this.controls.classList.remove('active');
      if (this.prevButton) this.prevButton.disabled = true;
      if (this.nextButton) this.nextButton.disabled = true;
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
        this.intersectionObserver = null;
      }
    }

    this.updateHeaderVisibility();
  }

  destroy() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }
}
